import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import {
  Dialog,
  DialogClose,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
} from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import AddRepositoryConnectPanel from './AddRepositoryConnectPanel';
import AddRepositoryPickPanel from './AddRepositoryPickPanel';
import AddRepositoryTestAutomationPanel from './AddRepositoryTestAutomationPanel';
import AddRepositoryScanPanel from './AddRepositoryScanPanel';
import AddRepositoryResultsPanel from './AddRepositoryResultsPanel';
import type { ResourceOption } from './addRepositoryTypes';
import type { AddRepositoryPickSelections } from './addRepositoryTypes';
import type { ScmProviderId } from '@/types/scm-provider';

type AddRepositoryDrawerProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

type ConnectPrefill = {
  provider: ScmProviderId;
  pat: string;
  organization?: string;
  organizationAccountId?: string;
  organizationAccountUri?: string;
  resources: ResourceOption[];
};

type DrawerPhase = 'connect' | 'pick' | 'test' | 'scanning' | 'results';

const AddRepositoryDrawer = ({
  open,
  onOpenChange,
}: AddRepositoryDrawerProps) => {
  const [draftPat, setDraftPat] = useState('');
  const [connected, setConnected] = useState<ConnectPrefill | null>(null);
  const [pickSelections, setPickSelections] =
    useState<AddRepositoryPickSelections | null>(null);
  const [phase, setPhase] = useState<DrawerPhase>('connect');
  const [scanElapsed, setScanElapsed] = useState('0:00');
  const [listingResetKey, setListingResetKey] = useState(0);
  const queryClient = useQueryClient();

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) {
      setDraftPat('');
      setConnected(null);
      setPickSelections(null);
      setPhase('connect');
      setScanElapsed('0:00');
    }
    onOpenChange(nextOpen);
  };

  const trackClassName = cn(
    'add-repo-panel-track',
    connected && 'is-connected',
    pickSelections && 'is-test-automation',
    phase === 'scanning' && 'is-scanning',
    phase === 'results' && 'is-results',
  );

  const handleBack = () => {
    if (phase === 'results') {
      setPhase('test');
      setScanElapsed('0:00');
      return;
    }
    if (phase === 'scanning') {
      setPhase('test');
      return;
    }
    if (pickSelections) {
      setPickSelections(null);
      setPhase('pick');
      return;
    }
    if (connected) {
      setConnected(null);
      setPickSelections(null);
      setPhase('connect');
      setListingResetKey(key => key + 1);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogPortal>
        <DialogOverlay className="add-repo-drawer-overlay bg-black/30 backdrop-blur-none" />
        <DialogPrimitive.Content
          aria-describedby={undefined}
          data-add-repo-drawer=""
          className="add-repo-drawer-panel fixed inset-y-0 right-0 z-[2001] flex h-full w-[min(42rem,92vw)] flex-col border-0 border-l border-solid border-[var(--inv-border)] bg-[var(--inv-bg)] p-0 shadow-[-16px_0_40px_rgba(0,0,0,0.12)] outline-none">
          <DialogTitle className="sr-only">Add repository</DialogTitle>
          <DialogClose
            data-add-repo-close=""
            className={cn(
              'absolute right-5 top-5 z-[2]',
              'flex h-8 w-8 items-center justify-center rounded-full',
              'border-0 bg-transparent p-0 shadow-none outline-none',
              'text-[var(--inv-text-muted)]',
              'transition-[color,background-color] duration-150',
              'hover:bg-[var(--inv-surface-muted)] hover:text-[var(--inv-text)]',
              'focus-visible:ring-2 focus-visible:ring-[var(--inv-accent-ring)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--inv-bg)]',
              'disabled:pointer-events-none',
            )}>
            <X className="h-4 w-4" strokeWidth={1.75} aria-hidden />
            <span className="sr-only">Close</span>
          </DialogClose>
          <div className="min-h-0 flex-1 overflow-hidden">
            {open ? (
              <div className={trackClassName}>
                <div className="add-repo-panel-pane">
                  <AddRepositoryConnectPanel
                    token={draftPat}
                    onTokenChange={setDraftPat}
                    resetListingKey={listingResetKey}
                    onConnect={(
                      provider,
                      pat,
                      organization,
                      resources,
                      organizationAccountId,
                      organizationAccountUri,
                    ) => {
                      setConnected({
                        provider,
                        pat,
                        organization,
                        organizationAccountId,
                        organizationAccountUri,
                        resources,
                      });
                      setPhase('pick');
                    }}
                  />
                </div>
                <div className="add-repo-panel-pane overflow-hidden">
                  {connected ? (
                    <AddRepositoryPickPanel
                      provider={connected.provider}
                      organization={connected.organization ?? ''}
                      organizationAccountId={connected.organizationAccountId}
                      organizationAccountUri={connected.organizationAccountUri}
                      pat={connected.pat}
                      items={connected.resources}
                      onBack={handleBack}
                      onContinue={selections => {
                        setPickSelections(selections);
                        setPhase('test');
                      }}
                    />
                  ) : null}
                </div>
                <div className="add-repo-panel-pane overflow-hidden">
                  {connected && pickSelections ? (
                    <AddRepositoryTestAutomationPanel
                      pick={pickSelections}
                      onBack={handleBack}
                      onOnboardComplete={() => setPhase('scanning')}
                    />
                  ) : null}
                </div>
                <div className="add-repo-panel-pane overflow-hidden">
                  {phase === 'scanning' || phase === 'results' ? (
                    <AddRepositoryScanPanel
                      onBack={handleBack}
                      onComplete={elapsed => {
                        setScanElapsed(elapsed);
                        setPhase('results');
                        void queryClient.invalidateQueries({
                          queryKey: ['component-list'],
                        });
                      }}
                    />
                  ) : null}
                </div>
                <div className="add-repo-panel-pane overflow-hidden">
                  {phase === 'results' ? (
                    <AddRepositoryResultsPanel
                      elapsedTime={scanElapsed}
                      onBack={handleBack}
                      onFinish={() => handleOpenChange(false)}
                    />
                  ) : null}
                </div>
              </div>
            ) : null}
          </div>
        </DialogPrimitive.Content>
      </DialogPortal>
    </Dialog>
  );
};

export default AddRepositoryDrawer;
